import type { AiOperationsCurrentProposal, AiOperationsRun } from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { ControlButton } from "@/components/patterns/control-button";
import { formatRunFailureReason } from "@/lib/ai-run-presentation";

export function NoChangesResult({
  onOpenAnalysis,
  proposal,
}: {
  onOpenAnalysis: () => void;
  proposal: AiOperationsCurrentProposal;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  return (
    <section className="flex min-h-[220px] items-center justify-start rounded-[10px] border border-line bg-surface px-8 py-7">
      <div className="flex max-w-[880px] flex-col gap-2.5">
        <p className="text-xs font-semibold leading-4 text-green-11">
          {t("runDetail.decisionRecord.noChanges.complete")}
        </p>
        <h2 className="text-2xl font-semibold leading-8 tracking-[-0.02em] text-ink">
          {t("runDetail.decisionRecord.noChanges.title")}
        </h2>
        <p className="text-sm leading-[22px] text-muted-foreground">
          {t("runDetail.decisionRecord.noChanges.description")}
        </p>
        <div className="flex items-center gap-2 pt-1.5">
          <ControlButton controlSize="default" onClick={onOpenAnalysis}>
            {t("runDetail.decisionRecord.summary.fullAnalysis")}
          </ControlButton>
          {proposal.artifact.conclusions.length > 0 ? (
            <ControlButton controlSize="toolbar" variant="outline" onClick={onOpenAnalysis}>
              {t("runDetail.decisionRecord.noChanges.observations", {
                count: proposal.artifact.conclusions.length,
              })}
            </ControlButton>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export function FailedRunResult({
  onOpenAnalysis,
  proposal,
  run,
}: {
  onOpenAnalysis: () => void;
  proposal: AiOperationsCurrentProposal;
  run: AiOperationsRun;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const reason = formatRunFailureReason(run.failureReason ?? "", run.autoExecuted, t);
  return (
    <section className="flex min-h-[220px] items-center justify-start rounded-[10px] border border-red/25 bg-surface px-8 py-7">
      <div className="flex max-w-[880px] flex-col gap-2.5">
        <p className="text-xs font-semibold leading-4 text-red-11">
          {t("runDetail.decisionRecord.failure.eyebrow")}
        </p>
        <h2 className="text-2xl font-semibold leading-8 tracking-[-0.02em] text-ink">
          {t("runDetail.failed.title")}
        </h2>
        <p className="text-sm leading-[22px] text-muted-foreground">{reason}</p>
        <div className="pt-1.5">
          <ControlButton controlSize="toolbar" variant="outline" onClick={onOpenAnalysis}>
            {t("runDetail.decisionRecord.failure.viewAnalysis", {
              count: proposal.artifact.conclusions.length,
            })}
          </ControlButton>
        </div>
      </div>
    </section>
  );
}
